import { useId } from "react";
import { Link } from "react-router-dom";
import type { AIActionApprovalRef, AIActionProposal, AIResourceRef } from "@k8s-dashboard/contracts";
import { useT } from "@/i18n/I18nProvider";
import { ActionApproval } from "./ActionApproval";
import { ACTION_RISK_GLYPH, ACTION_RISK_MESSAGE, ACTION_VERB_MESSAGE } from "./labels";
import { referenceHref } from "./referenceHref";

/**
 * Action Card — ADR 0027 기능 2 · Issue #19 Phase B
 * --------------------------------------------------------------------------
 * **제안 전용입니다.** 승인 버튼도, 실행 요청도, action을 바꾸는 폼도 이 파일에
 * 존재하지 않습니다. 그 표면은 Phase C(ADR 0028)에서 실행 API·감사·RBAC과 함께
 * 열립니다. 지금 여는 것은 단계 분리 원칙을 깨는 일입니다.
 *
 * 네 가지 규칙이 이 파일의 형태를 정합니다.
 *
 *   문구는 닫힘   동사·위험도는 계약의 닫힌 집합이고 화면이 문구를 고릅니다.
 *                모델이 쓴 문자열이 라벨이 되는 경로는 없습니다. (ADR 0025)
 *   링크는 UID    대상은 UID로 고르고, 표시 이름은 **서버가 조회에 성공한 참조**에서
 *                빌려옵니다. 계약에 이름이 없는 이유이자, 지어낸 이름으로 링크를
 *                만들지 않는 방법입니다. 짝이 없으면 링크가 아니라 텍스트입니다.
 *   색만으로 X    위험도는 아이콘 + 텍스트 라벨을 항상 함께 씁니다. (design-system)
 *   낭독은 하나   카드 어디에도 live region이 없습니다. 진행 상태만 중앙 Announcer로
 *                가고, 카드는 조용히 나타납니다.
 */

export interface ActionCardListProps {
  actions?: AIActionProposal[];
  /** 서버가 조회에 성공한 참조. 대상 이름은 여기에서만 옵니다. */
  references?: AIResourceRef[];
  /**
   * 승인 가능한 제안의 서버 발급 레코드 id. (ADR 0028 Phase C)
   *
   * 없으면 카드는 **제안 전용**입니다 — Phase C가 꺼져 있거나 이 사용자에게 실행
   * 능력이 없다는 뜻이고, 그 판단의 정본은 서버입니다.
   */
  approvals?: AIActionApprovalRef[];
  search: string;
}

export function ActionCardList({ actions, references, approvals, search }: ActionCardListProps) {
  const { t } = useT("agent");
  if (!actions || actions.length === 0) return null;
  /* 승인 참조의 **존재 자체**가 서버의 능력 신호입니다. 서버는 이 배포에 승인 경로가
     있고 이 사용자가 실행 능력을 가질 때만 참조를 싣습니다(handlers.go). 화면이 같은
     판단을 한 번 더 하려면 추가 요청이 필요한데, 그 요청은 안전을 더하지 않고
     "카드가 스스로 권한을 묻는다"는 다른 계약을 만듭니다. */
  const recordIds = new Map((approvals ?? []).map((ref) => [ref.proposalId, ref.recordId]));
  return (
    <ul className="ds-action-card-list" aria-label={t("agent.actions.aria")} data-testid="agent-actions">
      {actions.map((action) => (
        <li key={action.id}>
          <ActionCard
            action={action}
            references={references}
            recordId={recordIds.get(action.id)}
            search={search}
          />
        </li>
      ))}
    </ul>
  );
}

function ActionCard({
  action,
  references,
  recordId,
  search,
}: {
  action: AIActionProposal;
  references?: AIResourceRef[];
  recordId?: string;
  search: string;
}) {
  const { t } = useT("agent");
  const headingId = useId();
  return (
    <article className="ds-action-card" aria-labelledby={headingId} data-testid="agent-action-card">
      <header className="ds-action-card__head">
        <h3 id={headingId} className="ds-action-card__verb" data-testid="agent-action-verb">
          {t(ACTION_VERB_MESSAGE[action.verb], verbValues(action))}
        </h3>
        <RiskBadge risk={action.risk} />
      </header>
      <ActionTarget action={action} references={references} search={search} />
      <dl className="ds-action-card__facts">
        <dt>{t("agent.action.rationale")}</dt>
        {/* 텍스트로만 그립니다 — HTML도 Markdown도 해석하지 않습니다. */}
        <dd data-testid="agent-action-rationale">{action.rationale}</dd>
        <dt>{t("agent.action.expectedEffect")}</dt>
        <dd data-testid="agent-action-effect">{action.expectedEffect}</dd>
      </dl>
      {recordId ? (
        /* 승인 경로가 열린 사용자에게만 컨트롤이 붙습니다. 카드 본문은 그대로이고,
           바뀌는 것은 "실행 경로가 없다"는 안내가 승인 컨트롤로 바뀌는 것뿐입니다. */
        <ActionApproval recordId={recordId} />
      ) : (
        <p className="ds-action-card__notice" data-testid="agent-action-notice">
          {t("agent.action.suggestionOnly")}
        </p>
      )}
    </article>
  );
}

/**
 * 동사 문구에 채울 값.
 *
 * 계약 검증을 통과한 정수와 RFC 1123 이름뿐입니다 — 자유 문자열은 여기 올 수
 * 없으므로 문구가 모델 출력으로 오염되지 않습니다.
 */
function verbValues(action: AIActionProposal): Record<string, string> {
  switch (action.verb) {
    case "SCALE_REPLICAS":
      return { replicas: String(action.parameters.replicas) };
    case "ROLLBACK_REVISION":
      return { revision: String(action.parameters.revision) };
    case "ADJUST_RESOURCES":
      return { container: action.parameters.containerName };
    default:
      return {};
  }
}

/** 위험도는 색·글리프·텍스트 세 채널을 항상 함께 씁니다. */
function RiskBadge({ risk }: { risk: AIActionProposal["risk"] }) {
  const { t } = useT("agent");
  return (
    <span className={`ds-action-card__risk ds-action-card__risk--${risk}`} data-testid="agent-action-risk">
      <span className="ds-action-card__risk-glyph" aria-hidden="true">
        {ACTION_RISK_GLYPH[risk]}
      </span>
      <span className="ds-action-card__risk-label">
        {t("agent.action.risk", { level: t(ACTION_RISK_MESSAGE[risk]) })}
      </span>
    </span>
  );
}

/**
 * 대상 칩.
 *
 * 신원은 `target.workloadUid`이고, 이름은 **같은 cluster·namespace·UID로 서버가
 * 조회에 성공한 참조**에서만 옵니다. 짝이 없으면 링크를 만들지 않습니다 — 없는
 * 대상을 가리키는 링크보다 링크가 없는 편이 정직합니다.
 */
function ActionTarget({
  action,
  references,
  search,
}: {
  action: AIActionProposal;
  references?: AIResourceRef[];
  search: string;
}) {
  const { t } = useT("agent");
  const target = action.target;
  const reference = (references ?? []).find(
    (candidate) =>
      candidate.kind === "workload" &&
      candidate.uid === target.workloadUid &&
      candidate.clusterId === target.clusterId &&
      candidate.namespace === target.namespace,
  );
  if (!reference) {
    return (
      <p className="ds-action-card__target" data-testid="agent-action-target">
        {t("agent.action.targetUnlinked", { kind: target.workloadKind, namespace: target.namespace })}
      </p>
    );
  }
  return (
    <p className="ds-action-card__target">
      <Link className="ds-action-card__link" to={referenceHref(reference, search)} data-testid="agent-action-target">
        {t("agent.action.target", { kind: target.workloadKind, name: reference.name })}
      </Link>
    </p>
  );
}
